import { useI18N } from "@sundae/react-hooks";
import { Button, Image, Text, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, memo, useCallback } from "react";
import { MouseParallax } from "react-just-parallax";

import { useNavigate } from "@tanstack/react-location";
import { testIds } from "../../../constants/testIds.constants";
import { useIsWalletConnected } from "../../../hooks/useIsWalletConnected";
import { PortfolioTab, TLocationGenerics, paths } from "../../../pages/routes";
import ConnectWalletButton from "../../ConnectWalletButton";

const TTJumboTron: FC = () => {
  const { t } = useI18N("farms");
  const navigate = useNavigate<TLocationGenerics>();
  const isWalletConnected = useIsWalletConnected();
  const activeTasteTestsTable =
    document.getElementById(testIds.yieldFarming.landingPage.topFarms) || null;

  const handleScrollToActiveTasteTestsTable = useCallback(() => {
    if (activeTasteTestsTable) {
      activeTasteTestsTable.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "start",
      });
    }
  }, [activeTasteTestsTable]);

  const handleManagePositions = useCallback(() => {
    navigate({ to: paths.root, search: { tab: PortfolioTab.yieldFarming } });
  }, []);

  return (
    <div className="flex flex-wrap items-center gap-4 lg:flex-nowrap lg:gap-4">
      <div
        className={cx(
          "group relative order-2 w-full flex-shrink-0 overflow-hidden rounded-lg p-[1px]",
          "lg:order-1 lg:w-1/2",
          "hover:bg-gradient-to-r hover:from-secondary hover:to-primary",
          toolkitCx.layout.transition.all,
          toolkitCx.layout.background.default,
          toolkitCx.recipes.blurryCorner.primary
        )}
      >
        <div className="invisible absolute -bottom-52 -left-10 -right-10 -top-52 bg-gradient-to-r from-transparent via-white/90 to-transparent group-hover:visible group-hover:animate-spin-slow" />

        <div
          className={cx(
            "relative px-8 py-12",
            // rounded-lg - 1px for smoother corners
            "rounded-[8px]",
            toolkitCx.layout.background.default
          )}
        >
          <div className="relative z-10 space-y-4">
            <Text className="w-4/5" size="4xl" tag="h1" weight="semibold">
              {t("landingPage.hero.title")}{" "}
              <Text gradient size="4xl" tag="span" weight="semibold">
                {t("yieldFarming", { ns: "glossary" })}
              </Text>
              .
            </Text>
            <Text tag="p">{t("landingPage.hero.copy")}</Text>

            <div className="flex items-center justify-between gap-4">
              {isWalletConnected ? (
                <div className="flex-1">
                  <Button className="w-full" variant="secondary" onClick={handleManagePositions}>
                    {t("landingPage.manageYourPositions")}
                  </Button>
                </div>
              ) : (
                <Button
                  className="flex-1"
                  onClick={handleScrollToActiveTasteTestsTable}
                  variant="secondary"
                >
                  {t("landingPage.hero.primaryAction")}
                </Button>
              )}

              {isWalletConnected ? (
                <Button
                  className="flex-1"
                  onClick={handleScrollToActiveTasteTestsTable}
                  variant="colored"
                >
                  {t("landingPage.hero.primaryAction")}
                </Button>
              ) : (
                <ConnectWalletButton className="flex-1" size="lg" />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={cx("order-2 hidden h-fit w-1/2 items-center justify-center", "lg:flex")}>
        <MouseParallax strength={0.01}>
          <Image
            alt={t("home.disconnected", { ns: "alt-tags" })}
            className="mx-auto"
            height={498}
            src="/static/images/dudes/yield_farming_978x978.png"
            width={498}
          />
        </MouseParallax>
      </div>
    </div>
  );
};

export default memo(TTJumboTron);
